import { ServerCIEnvironment } from '~/servers/servers';

/**
 * A list of feature flags available
 *
 * These will be used as keys to locate the actual Unleash feature flag from the `envFeatureFlagMapping` constant
 *
 * When adding new feature flags, this `enum` must be updated
 */
export enum FeatureFlag {
    GLOBAL = 'global',
    CLIENT_TIMEZONE = 'client-timezone',
    CUSTOMER_NOTIFICATIONS = 'customer-notifications',
    ELD = 'eld',
    EV_BATTERY_REMAINING = 'ev-battery-remaining',
    INITIAL_VS_ACTUAL = 'initial-vs-actual',
    OPTIMIZATION_SUGGESTIONS = 'optimization-suggestions',
    REQUEST_EXTERNAL_ID_FOR_ROUTE_EXPORT = 'request-external-id-for-route-export',
    UNASSIGNED_TASKS_FOR_MULTIPLE_DAYS = 'unassigned-tasks-for-multiple-days'
}

/**
 * Pre-defined environment array for a single flag strategy
 */
const ANY_ENVIRONMENT = [ServerCIEnvironment.ANY];

/**
 * Pre-defined environment array for a separate environment flag strategy
 */
const DEFAULT_ENVIRONMENTS = [
    ServerCIEnvironment.STAGE,
    ServerCIEnvironment.UAT,
    ServerCIEnvironment.DEMO,
    ServerCIEnvironment.PRODUCTION
];

/**
 * A mapping of available Unleash feature flags to support CI environments
 *
 * This map is essential for the `getEnvFeatureFlag()` utility and `useFeatureFlag()` hook
 *
 * When adding new feature flags, this `object` must be updated
 *
 * @example
 * To use feature flags for specific environments, define as follows:
 * ```
 * [featureFlag]: [
 *     ServerCIEnvironment.STAGE,
 *     ServerCIEnvironment.CN_UAT,
 *     ServerCIEnvironment.PRODUCTION
 * ]
 * ```
 *
 * Using this strategy, you will need to define separate Unleash feature flags
 * for each environment you want to support.
 *
 * Environment feature flags must conform to the following convention:
 * ```
 * {featureFlag}-{environmentSuffix}
 * ```
 *
 * Supported suffixes are defined under `FeatureFlagEnvironmentSuffix`
 *
 * @example
 * To use feature flags for the default supported environments, define as follows:
 * ```
 * * [featureFlag]: DEFAULT_ENVIRONMENTS
 * ```
 *
 * This is the same as defining:
 * ```
 * [featureFlag]: [
 *     ServerCIEnvironment.STAGE,
 *     ServerCIEnvironment.UAT,
 *     ServerCIEnvironment.CN_UAT,
 *     ServerCIEnvironment.PRODUCTION
 * ]
 * ```
 *
 * @example
 * To just use 1 feature flag for any environment, define as follows:
 * ```
 * [featureFlag]: ANY_ENVIRONMENT
 * ```
 *
 * Using this strategy, you will need to define only one Unleash feature flag,
 * and configure for any environment as a single strategy.
 *
 * @see {@link https://gitlab.com/wisesystems/wise-web-multi-client/-/feature_flags | Available Interstellar Feature Flags}
 * @see {@link https://docs.getunleash.io/reference/sdks/react | Unleash React Documentation}
 */
export const envFeatureFlagMapping: Partial<
    Record<FeatureFlag, ServerCIEnvironment[]>
> = {
    [FeatureFlag.GLOBAL]: ANY_ENVIRONMENT,
    [FeatureFlag.CLIENT_TIMEZONE]: [
        ...DEFAULT_ENVIRONMENTS,
        ServerCIEnvironment.CN_UAT
    ],
    [FeatureFlag.CUSTOMER_NOTIFICATIONS]: DEFAULT_ENVIRONMENTS,
    [FeatureFlag.ELD]: DEFAULT_ENVIRONMENTS,
    [FeatureFlag.EV_BATTERY_REMAINING]: DEFAULT_ENVIRONMENTS,
    [FeatureFlag.INITIAL_VS_ACTUAL]: [ServerCIEnvironment.STAGE],
    [FeatureFlag.OPTIMIZATION_SUGGESTIONS]: [
        ServerCIEnvironment.STAGE,
        ServerCIEnvironment.UAT
    ],
    [FeatureFlag.REQUEST_EXTERNAL_ID_FOR_ROUTE_EXPORT]: [
        ServerCIEnvironment.PRODUCTION,
        ServerCIEnvironment.STAGE,
        ServerCIEnvironment.DEMO
    ],
    [FeatureFlag.UNASSIGNED_TASKS_FOR_MULTIPLE_DAYS]: DEFAULT_ENVIRONMENTS
};
